import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getBackendMode } from "../backend/client";
import { database } from "../storage/database";
import type { DraftRecord, SentLetterRecord } from "../types";

export function HomePage() {
  const [drafts, setDrafts] = useState<DraftRecord[]>([]);
  const [sent, setSent] = useState<SentLetterRecord[]>([]);
  useEffect(() => {
    void Promise.all([
      database.drafts.orderBy("updatedAt").reverse().limit(4).toArray(),
      database.sentLetters.orderBy("createdAt").reverse().limit(4).toArray(),
    ]).then(([draftRecords, sentRecords]) => {
      setDrafts(draftRecords);
      setSent(sentRecords);
    });
  }, []);

  return (
    <>
      <section className="hero">
        <div className="hero__copy">
          <p className="eyebrow">A slower kind of message</p>
          <h1>
            Write it by hand.
            <br />
            <em>Send it anywhere.</em>
          </h1>
          <p className="hero__lede">
            Turn the pages on your desk into a private digital letter—without
            flattening away the things that make it yours.
          </p>
          <div className="hero__actions">
            <Link className="button button--primary" to="/create">
              Create a letter <span aria-hidden="true">↗</span>
            </Link>
            <a className="text-link" href="#how-it-works">
              See how it works
            </a>
          </div>
          {getBackendMode() === "demo" ? (
            <p className="demo-note">
              <span /> Local demo mode · links open on this device
            </p>
          ) : null}
        </div>
        <div
          className="hero__artifact"
          aria-label="An example handwritten letter"
        >
          <div className="artifact-card artifact-card--back">
            <span>2</span>
          </div>
          <div className="artifact-card artifact-card--front">
            <div className="artifact-card__date">A quiet Tuesday</div>
            <p>Dear you,</p>
            <p className="handwriting">
              I wanted this to feel less like a notification and more like a
              small piece of my day arriving in yours.
            </p>
            <p className="handwriting handwriting--sign">Always, me</p>
          </div>
          <div className="artifact-seal">F</div>
        </div>
      </section>

      <section className="principle-strip" aria-label="Fold principles">
        <p>
          <span>01</span> Your handwriting stays handwriting.
        </p>
        <p>
          <span>02</span> Raw photos stay on your device.
        </p>
        <p>
          <span>03</span> No account needed to read.
        </p>
      </section>

      {drafts.length > 0 || sent.length > 0 ? (
        <section className="local-library" aria-labelledby="your-fold-title">
          <div className="section-heading">
            <p className="eyebrow">On this device</p>
            <h2 id="your-fold-title">Your Fold</h2>
          </div>
          <div className="library-grid">
            {drafts.map((draft) => (
              <Link
                className="library-card"
                key={draft.id}
                to={`/create/${draft.id}`}
              >
                <span>Draft</span>
                <strong>
                  {draft.title ||
                    `Letter to ${draft.recipientName || "someone"}`}
                </strong>
                <small>
                  {draft.pages.length} pages ·{" "}
                  {new Date(draft.updatedAt).toLocaleDateString()}
                </small>
              </Link>
            ))}
            {sent.map((letter) => (
              <Link
                className="library-card library-card--sent"
                key={letter.letterId}
                to={`/sent/${letter.letterId}`}
              >
                <span>Sent</span>
                <strong>
                  {letter.title ||
                    `Letter to ${letter.recipientName || "someone"}`}
                </strong>
                <small>
                  Private until{" "}
                  {new Date(letter.expiresAt).toLocaleDateString()}
                </small>
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      <section
        className="how-it-works"
        id="how-it-works"
        aria-labelledby="how-title"
      >
        <div className="section-heading">
          <p className="eyebrow">Three quiet steps</p>
          <h2 id="how-title">Paper in. Feeling intact.</h2>
        </div>
        <ol className="steps">
          <li>
            <span>01</span>
            <div>
              <h3>Capture</h3>
              <p>
                Photograph one page or ten. Fold straightens the page while the
                original stays safely on your device.
              </p>
            </div>
          </li>
          <li>
            <span>02</span>
            <div>
              <h3>Compose</h3>
              <p>
                Lift shadows, preserve the paper, or place every ink stroke onto
                quiet digital stationery.
              </p>
            </div>
          </li>
          <li>
            <span>03</span>
            <div>
              <h3>Send privately</h3>
              <p>
                Your browser encrypts the finished letter. The complete link is
                the only key to open it.
              </p>
            </div>
          </li>
        </ol>
      </section>
    </>
  );
}
